<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import {
  parseTaskFromMessage,
  updateTaskStatus,
} from 'dashboard/helper/tasksHelper';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TaskItem from './TaskItem.vue';
import TaskFormModal from './TaskFormModal.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const alert = useAlert();

const tasks = ref([]);
const isLoading = ref(false);
const showCreateModal = ref(false);
const editingTask = ref(null);

const currentUser = useMapGetter('getCurrentUser');

// Contador de tareas pendientes
const pendingTasksCount = computed(() => {
  return tasks.value.filter(task => !task.isCompleted).length;
});

// Cargar tareas de la conversación
const loadTasks = async () => {
  isLoading.value = true;
  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId: props.conversationId,
    });

    if (response && response.data && response.data.payload) {
      tasks.value = response.data.payload
        .filter(msg => msg.private && msg.content?.startsWith('[TAREA]'))
        .map(msg => parseTaskFromMessage(msg))
        .filter(Boolean)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  } catch (error) {
    alert.error(t('TASKS.ERROR_LOADING'));
  } finally {
    isLoading.value = false;
  }
};

// Crear nueva tarea
const handleCreateTask = () => {
  editingTask.value = null;
  showCreateModal.value = true;
};

// Editar tarea
const handleEditTask = task => {
  editingTask.value = task;
  showCreateModal.value = true;
};

// Eliminar tarea
const handleDeleteTask = async task => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('TASKS.DELETE_CONFIRM'))) return;

  try {
    await MessageApi.delete(props.conversationId, task.id);
    await loadTasks();
  } catch (error) {
    alert.error(t('TASKS.ERROR_DELETING'));
  }
};

// Cambiar estado de tarea
const handleStatusChange = async (task, newStatus) => {
  try {
    await updateTaskStatus(task, newStatus);
    await loadTasks();
  } catch (error) {
    alert.error(t('TASKS.ERROR_UPDATING'));
  }
};

// Guardar tarea (crear o editar)
const handleSaveTask = async taskData => {
  try {
    let emoji = '⏸️';
    if (taskData.status === 'Completada') emoji = '✅';
    else if (taskData.status === 'En Proceso') emoji = '🔄';

    const taskMessage = `[TAREA] ${taskData.title}
📋 Descripción: ${taskData.description || 'Sin descripción'}
📅 Vence: ${taskData.dueDate}
👤 Asignado: @${taskData.assignedTo || currentUser.value.name}
${emoji} Estado: ${taskData.status}`;

    await MessageApi.create({
      conversationId: props.conversationId,
      message: taskMessage,
      private: true,
    });

    showCreateModal.value = false;
    await loadTasks();
  } catch (error) {
    alert.error(t('TASKS.ERROR_SAVING'));
  }
};

// Cargar tareas al montar y cuando cambia la conversación
onMounted(() => {
  loadTasks();
});

watch(
  () => props.conversationId,
  () => {
    loadTasks();
  }
);
</script>

<template>
  <div>
    <!-- Header con contador y botón crear -->
    <div class="flex items-center justify-between p-2 pb-1">
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ $t('TASKS.PENDING_COUNT') }}
        </span>
        <span
          v-if="pendingTasksCount > 0"
          class="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-red-500 rounded-full"
        >
          {{ pendingTasksCount }}
        </span>
      </div>
      <NextButton
        faded
        xs
        icon="i-lucide-plus"
        :label="$t('TASKS.CREATE')"
        @click="handleCreateTask"
      />
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center gap-2 justify-center p-6 text-n-slate-12"
    >
      <span class="text-sm">{{ $t('TASKS.LOADING') }}</span>
      <Spinner class="size-5" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!tasks.length"
      class="p-3 text-center text-sm text-n-slate-11"
    >
      {{ $t('TASKS.NO_TASKS') }}
    </div>

    <!-- Lista de tareas -->
    <div v-else class="p-1 space-y-2">
      <TaskItem
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        @edit="handleEditTask"
        @delete="handleDeleteTask"
        @status-change="handleStatusChange"
      />
    </div>

    <!-- Modal de crear/editar -->
    <TaskFormModal
      v-if="showCreateModal"
      :task="editingTask"
      @save="handleSaveTask"
      @close="showCreateModal = false"
    />
  </div>
</template>
